import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, ShoppingCart, Users, IndianRupee, Upload, Truck, Tag, Sparkles, Mail, Phone, X, AlertTriangle, Undo2 } from "lucide-react";
import BulkActions from "@/components/admin/BulkActions";

const EMPTY_PRODUCT = {
  name: "", description: "", price: 0, category: "sweaters",
  images: [""], colors: [], sizes: [], stock: 10, featured: false, tags: [],
};

const EMPTY_COUPON = {
  code: "", description: "", discount_type: "flat", discount_value: 100,
  min_order: 0, max_discount: null, active: true,
};

const ORDER_STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const ADMIN_CANCEL_REASONS = [
  { v: "out-of-stock", l: "Item out of stock" },
  { v: "address-invalid", l: "Address could not be verified" },
  { v: "payment-failed", l: "Payment failed" },
  { v: "customer-requested", l: "Customer requested cancellation" },
  { v: "courier-unavailable", l: "Courier unavailable for this pincode" },
  { v: "suspected-fraud", l: "Suspected fraudulent order" },
  { v: "other", l: "Other" },
];
const RETURN_STATUSES = ["requested", "approved", "picked_up", "received", "quality_check", "refund_initiated", "refunded", "rejected"];
const CO_STATUSES = ["new", "contacted", "quoted", "in-progress", "completed", "cancelled"];
const CO_STATUS_COLORS = {
  "new": "bg-rani text-white",
  "contacted": "bg-marigold/30 text-deepbrown",
  "quoted": "bg-blue-100 text-blue-700",
  "in-progress": "bg-emerald/20 text-emerald",
  "completed": "bg-emerald text-white",
  "cancelled": "bg-deepbrown/20 text-deepbrown/60",
};

export default function Admin() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackingId, setTrackingId] = useState("");
  const [carrier, setCarrier] = useState("Shiprocket");
  const [uploading, setUploading] = useState(false);
  const [customOrders, setCustomOrders] = useState([]);
  const [coDetail, setCoDetail] = useState(null);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNotes, setCancelNotes] = useState("");
  const [returns, setReturns] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [reassignTo, setReassignTo] = useState("");

  // Per-tab selection sets for bulk actions (select-all / cancel / delete).
  const [selOrders, setSelOrders] = useState(new Set());
  const [selProducts, setSelProducts] = useState(new Set());
  const [selCoupons, setSelCoupons] = useState(new Set());
  const [selCustom, setSelCustom] = useState(new Set());
  const [selReturns, setSelReturns] = useState(new Set());
  const [selCategories, setSelCategories] = useState(new Set());

  const toggle = (setState) => (id) => setState((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = (setState, rows) => (checked) =>
    setState(checked ? new Set(rows.map((r) => r.id)) : new Set());

  const bulkDelete = async (label, ids, path, refresh, clearSel) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} ${label}? This cannot be undone.`)) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.delete(`${path}/${id}`); ok += 1; } catch { fail += 1; }
    }
    clearSel();
    await refresh();
    if (fail === 0) toast.success(`Deleted ${ok} ${label}`);
    else toast.error(`Deleted ${ok}, failed ${fail}`);
  };

  const bulkCancelOrders = async () => {
    const ids = Array.from(selOrders);
    if (ids.length === 0) return;
    if (!window.confirm(`Cancel ${ids.length} order(s)? Stock will be restored and customers notified.`)) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.put(`/orders/${id}/admin-cancel`, { reason: "other", notes: "Bulk cancel by admin" });
        ok += 1;
      } catch { fail += 1; }
    }
    setSelOrders(new Set());
    await loadAll();
    if (fail === 0) toast.success(`Cancelled ${ok} order(s)`);
    else toast.error(`Cancelled ${ok}, failed ${fail}`);
  };

  const loadAll = useCallback(async () => {
    const [s, p, o, c, cp, co, rt] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/products", { params: { limit: 200 } }),
      api.get("/orders"),
      api.get("/categories"),
      api.get("/coupons"),
      api.get("/custom-orders"),
      api.get("/admin/returns"),
    ]);
    setStats(s.data); setProducts(p.data); setOrders(o.data); setCategories(c.data); setCoupons(cp.data); setCustomOrders(co.data); setReturns(rt.data);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    // Non-admin users are NOT auto-redirected — we render an explanation
    // (see the render below). This prevents the "blank page → mysterious redirect"
    // confusion that admin gets when logged in as a non-admin account by mistake.
    if (user.role !== "admin") return;
    loadAll();
  }, [user, authLoading, navigate, loadAll]);

  const updateReturnStatus = async (orderId, status) => {
    const note = status === "rejected" ? (window.prompt("Reason for rejection (will be emailed to customer):") || "") : "";
    if (status === "rejected" && !note.trim()) return toast.error("Rejection note required");
    try {
      await api.put(`/orders/${orderId}/return-status`, { status, note });
      toast.success(`Marked ${status.replace(/_/g, " ")}`);
      loadAll();
    } catch (e) { toast.error("Failed"); }
  };

  const saveCategory = async () => {
    if (!editingCat?.name?.trim()) return toast.error("Name required");
    const isExisting = !!(editingCat.id && categories.find((c) => c.id === editingCat.id));
    try {
      if (isExisting) {
        await api.put(`/categories/${editingCat.id}`, { name: editingCat.name, icon: editingCat.icon, sort_order: Number(editingCat.sort_order || 99) });
      } else {
        await api.post("/categories", { name: editingCat.name, icon: editingCat.icon || "Tag", sort_order: Number(editingCat.sort_order || 99) });
      }
      toast.success("Category saved");
      setCatOpen(false); setEditingCat(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteCategory = async (cat) => {
    const inUse = products.filter((p) => p.category === cat.id).length;
    if (inUse === 0) {
      if (!window.confirm(`Delete category "${cat.name}"?`)) return;
      try {
        await api.delete(`/categories/${cat.id}`);
        toast.success("Deleted");
        loadAll();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
      return;
    }
    const others = categories.filter((c) => c.id !== cat.id);
    if (others.length === 0) return toast.error("Create another category first — this is the only one and it has products.");
    const target = window.prompt(
      `"${cat.name}" has ${inUse} product(s). Type a category ID to move them to, then delete:\n\n` +
      others.map((c) => `• ${c.id}  (${c.name})`).join("\n"),
      others[0].id
    );
    if (!target) return;
    if (!others.find((c) => c.id === target)) return toast.error("Invalid target category");
    try {
      await api.delete(`/categories/${cat.id}?reassign_to=${encodeURIComponent(target)}`);
      toast.success(`Moved ${inUse} product(s) → ${target} and deleted`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openNew = () => { setEditing({ ...EMPTY_PRODUCT, _new: true }); setOpen(true); };
  const openEdit = (p) => { setEditing({ ...p }); setOpen(true); };

  const saveProduct = async () => {
    const payload = {
      name: editing.name,
      description: editing.description,
      price: Number(editing.price),
      category: editing.category,
      images: typeof editing.images === "string" ? editing.images.split("\n").filter(Boolean) : editing.images,
      colors: typeof editing.colors === "string" ? editing.colors.split(",").map(s => s.trim()).filter(Boolean) : editing.colors,
      sizes: typeof editing.sizes === "string" ? editing.sizes.split(",").map(s => s.trim()).filter(Boolean) : editing.sizes,
      stock: Number(editing.stock),
      featured: Boolean(editing.featured),
      tags: typeof editing.tags === "string" ? editing.tags.split(",").map(s => s.trim()).filter(Boolean) : editing.tags,
    };
    try {
      if (editing._new) await api.post("/products", payload);
      else await api.put(`/products/${editing.id}`, payload);
      toast.success("Product saved");
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const updateOrderStatus = async (orderId, status) => {
    await api.put(`/orders/${orderId}/status`, { status });
    toast.success(`Order ${status}`);
    loadAll();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      const current = Array.isArray(editing.images) ? editing.images.filter(Boolean) : (editing.images || "").split("\n").filter(Boolean);
      setEditing({ ...editing, images: [...current, url] });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveCoupon = async () => {
    if (!editingCoupon.code.trim()) return toast.error("Code required");
    try {
      await api.post("/coupons", { ...editingCoupon, code: editingCoupon.code.toUpperCase().trim() });
      toast.success("Coupon created");
      setCouponOpen(false); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/coupons/${id}`);
    toast.success("Deleted"); loadAll();
  };

  const openTracking = (o) => {
    setTrackingOrder(o);
    setTrackingId(o.tracking_id || "");
    setCarrier(o.carrier || "Shiprocket");
  };

  const saveTracking = async () => {
    if (!trackingId.trim()) return toast.error("Tracking ID required");
    try {
      await api.put(`/orders/${trackingOrder.id}/tracking`, { tracking_id: trackingId.trim(), carrier });
      toast.success("Tracking saved · order marked shipped");
      setTrackingOrder(null); loadAll();
    } catch (e) { toast.error("Failed"); }
  };

  const updateCoStatus = async (id, status) => {
    try {
      await api.put(`/custom-orders/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      loadAll();
    } catch (e) { toast.error("Failed"); }
  };

  const submitAdminCancel = async () => {
    if (!cancelReason) return toast.error("Pick a reason");
    if (cancelReason === "other" && !cancelNotes.trim()) return toast.error("Notes required when reason is Other");
    try {
      await api.put(`/orders/${cancelOrder.id}/admin-cancel`, { reason: cancelReason, notes: cancelNotes });
      toast.success("Order cancelled · confirmation email sent");
      setCancelOrder(null); setCancelReason(""); setCancelNotes("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (authLoading) return <div className="text-center py-20 font-display text-2xl text-deepbrown/60">Loading…</div>;
  if (!user) return <div className="text-center py-20 font-display text-2xl text-deepbrown/60">Redirecting to sign in…</div>;
  if (user.role !== "admin") {
    // Explicit, friendly access-denied screen — no silent redirect that looks like a
    // "blank/black page → home page" bounce. Gives the user options.
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="admin-access-denied">
        <div className="h-16 w-16 rounded-full bg-rani/10 grid place-items-center mx-auto mb-6">
          <AlertTriangle className="h-8 w-8 text-rani" />
        </div>
        <h1 className="font-display text-3xl text-deepbrown font-bold">Admin access required</h1>
        <p className="text-deepbrown/70 mt-3">
          You are signed in as <b>{user.email}</b>, which is a customer account — not an admin.
        </p>
        <p className="text-deepbrown/60 text-sm mt-2">
          Sign out and log back in with the admin credentials to access the dashboard.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="px-6 py-2.5 rounded-full bg-rani text-white font-bold hover:bg-rani/90"
            data-testid="admin-signout-btn"
          >
            Sign out & switch account
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2.5 rounded-full bg-white border border-deepbrown/20 text-deepbrown font-bold hover:border-deepbrown"
            data-testid="admin-back-home-btn"
          >
            Back to shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-rani font-bold">Admin</p>
          <h1 className="font-display text-4xl sm:text-5xl text-deepbrown font-bold mt-2">Dashboard</h1>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={IndianRupee} label="Revenue" value={`₹${(stats.revenue || 0).toLocaleString("en-IN")}`} color="bg-emerald" />
          <StatCard icon={ShoppingCart} label="Orders" value={stats.total_orders} color="bg-rani" />
          <StatCard icon={Package} label="Products" value={stats.total_products} color="bg-marigold" />
          <StatCard icon={Users} label="Customers" value={stats.total_users} color="bg-deepbrown" />
        </div>
      )}

      <Tabs defaultValue="orders" className="mt-10">
        <TabsList className="bg-white border border-deepbrown/10 rounded-full p-1">
          <TabsTrigger value="orders" className="rounded-full" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="products" className="rounded-full" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="coupons" className="rounded-full" data-testid="tab-coupons">Coupons</TabsTrigger>
          <TabsTrigger value="custom" className="rounded-full" data-testid="tab-custom-orders">
            Custom Orders
            {customOrders.filter((c) => c.status === "new").length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-rani text-white text-[10px] font-bold">
                {customOrders.filter((c) => c.status === "new").length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="returns" className="rounded-full" data-testid="tab-returns">
            Returns
            {returns.filter((r) => r.return_request?.status === "requested").length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-rani text-white text-[10px] font-bold">
                {returns.filter((r) => r.return_request?.status === "requested").length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="categories" className="rounded-full" data-testid="tab-categories">Categories</TabsTrigger>
        </TabsList>

        {/* Orders */}
        <TabsContent value="orders" className="mt-6">
          <BulkActions
            count={selOrders.size}
            itemName="order"
            onCancel={() => setSelOrders(new Set())}
            onCancelOrders={bulkCancelOrders}
            onDelete={() => bulkDelete("orders", Array.from(selOrders), "/orders", loadAll, () => setSelOrders(new Set()))}
          />
          <div className="bg-white rounded-2xl border border-deepbrown/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-marigold/10 text-deepbrown">
                  <tr>
                    <th className="p-4 w-10">
                      <input
                        type="checkbox"
                        checked={orders.length > 0 && selOrders.size === orders.length}
                        onChange={(e) => toggleAll(setSelOrders, orders)(e.target.checked)}
                        data-testid="orders-select-all"
                        aria-label="Select all orders"
                      />
                    </th>
                    <th className="text-left p-4">Order</th>
                    <th className="text-left p-4">Customer</th>
                    <th className="text-left p-4">Items</th>
                    <th className="text-left p-4">Total</th>
                    <th className="text-left p-4">Payment</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Tracking</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-deepbrown/10" data-testid={`admin-order-${o.id}`}>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selOrders.has(o.id)}
                          onChange={() => toggle(setSelOrders)(o.id)}
                          data-testid={`order-select-${o.id}`}
                          aria-label={`Select order ${o.order_number}`}
                        />
                      </td>
                      <td className="p-4 font-mono text-xs">{o.order_number}</td>
                      <td className="p-4">
                        <p className="font-semibold">{o.user_name}</p>
                        <p className="text-xs text-deepbrown/60">{o.user_email}</p>
                      </td>
                      <td className="p-4">{o.items.length}</td>
                      <td className="p-4 font-bold text-rani">₹{o.total.toLocaleString("en-IN")}</td>
                      <td className="p-4">
                        <span className="text-xs capitalize">{o.payment_method}</span>
                        <p className="text-[10px] text-deepbrown/60 capitalize">{o.payment_status}</p>
                      </td>
                      <td className="p-4">
                        <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.id, v)}>
                          <SelectTrigger className="w-32 rounded-full" data-testid={`status-select-${o.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <button onClick={() => openTracking(o)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-marigold/20 hover:bg-marigold/40 text-xs font-bold text-deepbrown" data-testid={`tracking-btn-${o.id}`}>
                          <Truck className="h-3 w-3" /> {o.tracking_id ? o.tracking_id.slice(0, 10) : "Add"}
                        </button>
                        {o.status !== "cancelled" && (
                          <button
                            onClick={() => { setCancelOrder(o); setCancelReason(""); setCancelNotes(""); }}
                            className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-rani/10 hover:bg-rani hover:text-white text-xs font-bold text-rani"
                            data-testid={`cancel-btn-${o.id}`}
                          >
                            <X className="h-3 w-3" /> Cancel
                          </button>
                        )}
                        {o.status === "cancelled" && o.cancellation && (
                          <p className="text-[10px] text-deepbrown/50 mt-1">{o.cancellation.reason}</p>
                        )}
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr><td colSpan="8" className="p-8 text-center text-deepbrown/60">No orders yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Products */}
        <TabsContent value="products" className="mt-6">
          <BulkActions
            count={selProducts.size}
            itemName="product"
            onCancel={() => setSelProducts(new Set())}
            onDelete={() => bulkDelete("products", Array.from(selProducts), "/products", loadAll, () => setSelProducts(new Set()))}
          />
          <div className="flex justify-end mb-4">
            <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rani text-white font-bold" data-testid="add-product-btn">
              <Plus className="h-4 w-4" /> New Product
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-deepbrown/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-marigold/10 text-deepbrown">
                  <tr>
                    <th className="p-4 w-10">
                      <input
                        type="checkbox"
                        checked={products.length > 0 && selProducts.size === products.length}
                        onChange={(e) => toggleAll(setSelProducts, products)(e.target.checked)}
                        data-testid="products-select-all"
                        aria-label="Select all products"
                      />
                    </th>
                    <th className="text-left p-4">Product</th>
                    <th className="text-left p-4">Category</th>
                    <th className="text-left p-4">Price</th>
                    <th className="text-left p-4">Stock</th>
                    <th className="text-left p-4">Featured</th>
                    <th className="text-left p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-deepbrown/10" data-testid={`admin-product-${p.id}`}>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selProducts.has(p.id)}
                          onChange={() => toggle(setSelProducts)(p.id)}
                          data-testid={`product-select-${p.id}`}
                          aria-label={`Select ${p.name}`}
                        />
                      </td>
                      <td className="p-4 flex items-center gap-3">
                        <img src={p.images[0]} className="h-10 w-10 rounded-lg object-cover" alt="" />
                        <span className="font-semibold">{p.name}</span>
                      </td>
                      <td className="p-4 capitalize">{p.category.replace("-", " ")}</td>
                      <td className="p-4 font-bold text-rani">₹{p.price.toLocaleString("en-IN")}</td>
                      <td className="p-4" data-testid={`admin-product-stock-${p.id}`}>
                        {p.stock > 5 ? (
                          <span className="inline-flex items-center gap-1.5 text-emerald font-bold"><span className="h-2 w-2 rounded-full bg-emerald"></span>{p.stock}</span>
                        ) : p.stock > 0 ? (
                          <span className="inline-flex items-center gap-1.5 text-marigold font-bold"><span className="h-2 w-2 rounded-full bg-marigold"></span>{p.stock} <span className="text-[10px] uppercase tracking-widest">low</span></span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-rani font-bold"><span className="h-2 w-2 rounded-full bg-rani"></span>0 <span className="text-[10px] uppercase tracking-widest">out</span></span>
                        )}
                      </td>
                      <td className="p-4">{p.featured ? <span className="text-emerald">★</span> : "—"}</td>
                      <td className="p-4 flex gap-2">
                        <button onClick={() => openEdit(p)} className="h-8 w-8 rounded-full bg-marigold/20 grid place-items-center hover:bg-marigold/40" data-testid={`edit-product-${p.id}`}>
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button onClick={() => deleteProduct(p.id)} className="h-8 w-8 rounded-full bg-rani/10 grid place-items-center hover:bg-rani hover:text-white" data-testid={`delete-product-${p.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Coupons */}
        <TabsContent value="coupons" className="mt-6">
          <BulkActions
            count={selCoupons.size}
            itemName="coupon"
            onCancel={() => setSelCoupons(new Set())}
            onDelete={() => bulkDelete("coupons", Array.from(selCoupons), "/coupons", loadAll, () => setSelCoupons(new Set()))}
          />
          <div className="flex justify-end mb-4">
            <button onClick={() => { setEditingCoupon({ ...EMPTY_COUPON }); setCouponOpen(true); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rani text-white font-bold" data-testid="add-coupon-btn">
              <Plus className="h-4 w-4" /> New Coupon
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-deepbrown/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-marigold/10 text-deepbrown">
                  <tr>
                    <th className="p-4 w-10">
                      <input
                        type="checkbox"
                        checked={coupons.length > 0 && selCoupons.size === coupons.length}
                        onChange={(e) => toggleAll(setSelCoupons, coupons)(e.target.checked)}
                        data-testid="coupons-select-all"
                        aria-label="Select all coupons"
                      />
                    </th>
                    <th className="text-left p-4">Code</th>
                    <th className="text-left p-4">Description</th>
                    <th className="text-left p-4">Discount</th>
                    <th className="text-left p-4">Min Order</th>
                    <th className="text-left p-4">Active</th>
                    <th className="text-left p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((c) => (
                    <tr key={c.id} className="border-t border-deepbrown/10" data-testid={`admin-coupon-${c.id}`}>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selCoupons.has(c.id)}
                          onChange={() => toggle(setSelCoupons)(c.id)}
                          data-testid={`coupon-select-${c.id}`}
                          aria-label={`Select coupon ${c.code}`}
                        />
                      </td>
                      <td className="p-4 font-mono font-bold text-rani">{c.code}</td>
                      <td className="p-4 text-deepbrown/70">{c.description}</td>
                      <td className="p-4 font-bold">
                        {c.discount_type === "flat" ? `₹${c.discount_value}` : `${c.discount_value}%${c.max_discount ? ` (max ₹${c.max_discount})` : ""}`}
                      </td>
                      <td className="p-4">₹{c.min_order || 0}</td>
                      <td className="p-4">{c.active ? <span className="text-emerald">●</span> : <span className="text-deepbrown/30">●</span>}</td>
                      <td className="p-4">
                        <button onClick={() => deleteCoupon(c.id)} className="h-8 w-8 rounded-full bg-rani/10 grid place-items-center hover:bg-rani hover:text-white" data-testid={`delete-coupon-${c.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {coupons.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-deepbrown/60">No coupons yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Custom Orders */}
        <TabsContent value="custom" className="mt-6">
          <BulkActions
            count={selCustom.size}
            itemName="inquiry"
            onCancel={() => setSelCustom(new Set())}
            onDelete={() => bulkDelete("inquiries", Array.from(selCustom), "/custom-orders", loadAll, () => setSelCustom(new Set()))}
          />
          <div className="bg-white rounded-2xl border border-deepbrown/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-marigold/10 text-deepbrown">
                  <tr>
                    <th className="p-4 w-10">
                      <input
                        type="checkbox"
                        checked={customOrders.length > 0 && selCustom.size === customOrders.length}
                        onChange={(e) => toggleAll(setSelCustom, customOrders)(e.target.checked)}
                        data-testid="custom-select-all"
                        aria-label="Select all custom orders"
                      />
                    </th>
                    <th className="text-left p-4">From</th>
                    <th className="text-left p-4">Occasion / Budget</th>
                    <th className="text-left p-4">Description</th>
                    <th className="text-left p-4">Received</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {customOrders.map((c) => (
                    <tr key={c.id} className="border-t border-deepbrown/10" data-testid={`admin-custom-${c.id}`}>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selCustom.has(c.id)}
                          onChange={() => toggle(setSelCustom)(c.id)}
                          data-testid={`custom-select-${c.id}`}
                          aria-label={`Select inquiry from ${c.name}`}
                        />
                      </td>
                      <td className="p-4">
                        <p className="font-semibold text-deepbrown flex items-center gap-1"><Sparkles className="h-3 w-3 text-rani" /> {c.name}</p>
                        <p className="text-xs text-deepbrown/60 flex items-center gap-1 mt-1"><Mail className="h-3 w-3" /> {c.email}</p>
                        {c.phone && <p className="text-xs text-deepbrown/60 flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</p>}
                      </td>
                      <td className="p-4 text-xs">
                        {c.occasion && <p>{c.occasion}</p>}
                        {c.budget && <p className="text-deepbrown/60">{c.budget}</p>}
                      </td>
                      <td className="p-4 max-w-sm">
                        <p className="text-xs text-deepbrown/80 line-clamp-2">{c.description}</p>
                        {c.color_preferences && <p className="text-[10px] text-marigold uppercase tracking-widest font-bold mt-1">Colors: {c.color_preferences}</p>}
                      </td>
                      <td className="p-4 text-xs text-deepbrown/60">
                        {new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        <br />
                        {new Date(c.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="p-4">
                        <Select value={c.status} onValueChange={(v) => updateCoStatus(c.id, v)}>
                          <SelectTrigger className={`w-32 rounded-full text-xs ${CO_STATUS_COLORS[c.status] || ""}`} data-testid={`co-status-${c.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <button onClick={() => setCoDetail(c)} className="px-3 py-1.5 rounded-full bg-deepbrown text-ivory text-xs font-bold hover:bg-rani" data-testid={`co-view-${c.id}`}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {customOrders.length === 0 && (
                    <tr><td colSpan="7" className="p-12 text-center text-deepbrown/60">
                      <Sparkles className="h-10 w-10 text-rani mx-auto mb-2" />
                      <p className="font-display text-xl">No custom orders yet</p>
                      <p className="text-xs mt-1">Customer requests from the homepage form will appear here.</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Returns */}
        <TabsContent value="returns" className="mt-6">
          <BulkActions
            count={selReturns.size}
            itemName="return"
            onCancel={() => setSelReturns(new Set())}
            hideDelete
          />
          <div className="bg-white rounded-2xl border border-deepbrown/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-marigold/10 text-deepbrown">
                  <tr>
                    <th className="p-4 w-10">
                      <input
                        type="checkbox"
                        checked={returns.length > 0 && selReturns.size === returns.length}
                        onChange={(e) => toggleAll(setSelReturns, returns)(e.target.checked)}
                        data-testid="returns-select-all"
                        aria-label="Select all returns"
                      />
                    </th>
                    <th className="text-left p-4">Order</th>
                    <th className="text-left p-4">Customer</th>
                    <th className="text-left p-4">Reason / Condition</th>
                    <th className="text-left p-4">Refund</th>
                    <th className="text-left p-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.map((o) => {
                    const r = o.return_request;
                    return (
                      <tr key={o.id} className="border-t border-deepbrown/10" data-testid={`admin-return-${o.id}`}>
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selReturns.has(o.id)}
                            onChange={() => toggle(setSelReturns)(o.id)}
                            data-testid={`return-select-${o.id}`}
                            aria-label={`Select return for ${o.order_number}`}
                          />
                        </td>
                        <td className="p-4">
                          <p className="font-mono text-xs">{o.order_number}</p>
                          <p className="text-[10px] text-deepbrown/60">{new Date(r.requested_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                        </td>
                        <td className="p-4">
                          <p className="font-semibold">{o.user_name}</p>
                          <p className="text-xs text-deepbrown/60">{o.user_email}</p>
                        </td>
                        <td className="p-4 max-w-sm">
                          <p className="text-xs text-deepbrown/80">{r.reason}</p>
                          <p className="text-[10px] text-marigold uppercase tracking-widest font-bold mt-1">{r.condition}</p>
                          {r.notes && <p className="text-[10px] text-deepbrown/50 mt-1 italic">&quot;{r.notes}&quot;</p>}
                        </td>
                        <td className="p-4">
                          <p className="font-bold text-rani">₹{(r.refund_amount || o.total).toLocaleString("en-IN")}</p>
                          <p className="text-[10px] text-deepbrown/60">{r.refund_method === "bank_transfer" ? `Bank · ${r.bank_account?.slice(-4) || "—"}` : "Original payment"}</p>
                          {r.bank_holder && <p className="text-[10px] text-deepbrown/50">{r.bank_holder} · {r.bank_ifsc}</p>}
                        </td>
                        <td className="p-4">
                          <Select value={r.status} onValueChange={(v) => updateReturnStatus(o.id, v)}>
                            <SelectTrigger className="w-44 rounded-full text-xs" data-testid={`return-status-${o.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {RETURN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                  {returns.length === 0 && (
                    <tr><td colSpan="6" className="p-12 text-center text-deepbrown/60">
                      <Undo2 className="h-10 w-10 text-rani mx-auto mb-2" />
                      <p className="font-display text-xl">No return requests</p>
                      <p className="text-xs mt-1">Customers can request returns within 7 days of shipping.</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Categories */}
        <TabsContent value="categories" className="mt-6">
          <BulkActions
            count={selCategories.size}
            itemName="category"
            onCancel={() => setSelCategories(new Set())}
            onDelete={() => bulkDelete("categories", Array.from(selCategories), "/categories", loadAll, () => setSelCategories(new Set()))}
          />
          <div className="flex justify-end mb-4">
            <button
              onClick={() => { setEditingCat({ name: "", icon: "Tag", sort_order: (categories.length + 1) }); setCatOpen(true); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rani text-white font-bold"
              data-testid="add-category-btn"
            >
              <Plus className="h-4 w-4" /> New Category
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-deepbrown/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-marigold/10 text-deepbrown">
                  <tr>
                    <th className="p-4 w-10">
                      <input
                        type="checkbox"
                        checked={categories.length > 0 && selCategories.size === categories.length}
                        onChange={(e) => toggleAll(setSelCategories, categories)(e.target.checked)}
                        data-testid="categories-select-all"
                        aria-label="Select all categories"
                      />
                    </th>
                    <th className="text-left p-4">Sort</th>
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">ID (slug)</th>
                    <th className="text-left p-4">Icon</th>
                    <th className="text-left p-4">Products</th>
                    <th className="text-left p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => {
                    const count = products.filter((p) => p.category === c.id).length;
                    return (
                      <tr key={c.id} className="border-t border-deepbrown/10" data-testid={`admin-category-${c.id}`}>
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selCategories.has(c.id)}
                            onChange={() => toggle(setSelCategories)(c.id)}
                            data-testid={`category-select-${c.id}`}
                            aria-label={`Select ${c.name}`}
                          />
                        </td>
                        <td className="p-4 font-bold">{c.sort_order ?? "—"}</td>
                        <td className="p-4 font-display text-deepbrown">{c.name}</td>
                        <td className="p-4 font-mono text-xs text-deepbrown/60">{c.id}</td>
                        <td className="p-4 text-xs">{c.icon}</td>
                        <td className="p-4">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${count > 0 ? "bg-emerald/15 text-emerald" : "bg-deepbrown/10 text-deepbrown/60"}`}>{count}</span>
                        </td>
                        <td className="p-4 flex gap-2">
                          <button
                            onClick={() => { setEditingCat({ ...c }); setCatOpen(true); }}
                            className="h-8 w-8 rounded-full bg-marigold/20 grid place-items-center hover:bg-marigold/40"
                            data-testid={`edit-category-${c.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => deleteCategory(c)}
                            className="h-8 w-8 rounded-full bg-rani/10 grid place-items-center hover:bg-rani hover:text-white"
                            data-testid={`delete-category-${c.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {categories.length === 0 && (
                    <tr><td colSpan="6" className="p-12 text-center text-deepbrown/60">No categories yet. Add one to start organizing products.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-deepbrown/50 mt-3">
            Tip: when you delete a category with products, you&apos;ll be asked to pick a target category — products move there automatically.
          </p>
        </TabsContent>
      </Tabs>

      {/* Edit/New product Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?._new ? "New Product" : "Edit Product"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="prod-name" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="prod-desc" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Price (₹)</Label>
                  <Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="prod-price" />
                </div>
                <div>
                  <Label>Stock</Label>
                  <Input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} data-testid="prod-stock" />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="prod-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Images</Label>
                  <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-marigold/20 hover:bg-marigold/40 text-xs font-bold text-deepbrown cursor-pointer" data-testid="upload-image-btn">
                    <Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Upload image"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ""; }}
                    />
                  </label>
                </div>
                <Textarea
                  value={Array.isArray(editing.images) ? editing.images.join("\n") : editing.images}
                  onChange={(e) => setEditing({ ...editing, images: e.target.value })}
                  rows={3}
                  placeholder="One URL per line. Use Upload to add from disk."
                  data-testid="prod-images"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Colors (comma separated)</Label>
                  <Input
                    value={Array.isArray(editing.colors) ? editing.colors.join(", ") : editing.colors}
                    onChange={(e) => setEditing({ ...editing, colors: e.target.value })}
                    data-testid="prod-colors"
                  />
                </div>
                <div>
                  <Label>Sizes (comma separated)</Label>
                  <Input
                    value={Array.isArray(editing.sizes) ? editing.sizes.join(", ") : editing.sizes}
                    onChange={(e) => setEditing({ ...editing, sizes: e.target.value })}
                    data-testid="prod-sizes"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between bg-marigold/10 p-3 rounded-xl">
                <Label className="m-0">Featured on homepage</Label>
                <Switch checked={Boolean(editing.featured)} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} data-testid="prod-featured" />
              </div>
              <button onClick={saveProduct} className="w-full py-3 rounded-full bg-rani text-white font-bold" data-testid="prod-save">
                Save
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Coupon Dialog */}
      <Dialog open={couponOpen} onOpenChange={setCouponOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New Coupon</DialogTitle>
          </DialogHeader>
          {editingCoupon && (
            <div className="space-y-3">
              <div>
                <Label>Code</Label>
                <Input value={editingCoupon.code} onChange={(e) => setEditingCoupon({ ...editingCoupon, code: e.target.value.toUpperCase() })} placeholder="WELCOME20" data-testid="coupon-code" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editingCoupon.description} onChange={(e) => setEditingCoupon({ ...editingCoupon, description: e.target.value })} data-testid="coupon-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={editingCoupon.discount_type} onValueChange={(v) => setEditingCoupon({ ...editingCoupon, discount_type: v })}>
                    <SelectTrigger data-testid="coupon-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat ₹</SelectItem>
                      <SelectItem value="percent">Percent %</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Value</Label>
                  <Input type="number" value={editingCoupon.discount_value} onChange={(e) => setEditingCoupon({ ...editingCoupon, discount_value: Number(e.target.value) })} data-testid="coupon-value" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min order ₹</Label>
                  <Input type="number" value={editingCoupon.min_order} onChange={(e) => setEditingCoupon({ ...editingCoupon, min_order: Number(e.target.value) })} data-testid="coupon-min" />
                </div>
                <div>
                  <Label>Max discount ₹ (optional)</Label>
                  <Input type="number" value={editingCoupon.max_discount || ""} onChange={(e) => setEditingCoupon({ ...editingCoupon, max_discount: e.target.value ? Number(e.target.value) : null })} data-testid="coupon-max" />
                </div>
              </div>
              <button onClick={saveCoupon} className="w-full py-3 rounded-full bg-rani text-white font-bold" data-testid="coupon-save">Save Coupon</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog open={!!trackingOrder} onOpenChange={(v) => !v && setTrackingOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Shipping & Tracking</DialogTitle>
          </DialogHeader>
          {trackingOrder && (
            <div className="space-y-3">
              <p className="text-sm text-deepbrown/70">Order <b>{trackingOrder.order_number}</b> · ₹{trackingOrder.total.toLocaleString("en-IN")}</p>
              <div>
                <Label>Carrier</Label>
                <Select value={carrier} onValueChange={setCarrier}>
                  <SelectTrigger data-testid="tracking-carrier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Shiprocket">Shiprocket</SelectItem>
                    <SelectItem value="Delhivery">Delhivery</SelectItem>
                    <SelectItem value="DTDC">DTDC</SelectItem>
                    <SelectItem value="India Post">India Post</SelectItem>
                    <SelectItem value="Bluedart">Bluedart</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tracking ID / AWB</Label>
                <Input value={trackingId} onChange={(e) => setTrackingId(e.target.value)} placeholder="e.g. SR123456789IN" data-testid="tracking-id" />
              </div>
              <button onClick={saveTracking} className="w-full py-3 rounded-full bg-rani text-white font-bold" data-testid="tracking-save">
                Save · Mark Shipped
              </button>
              <p className="text-[11px] text-deepbrown/50 text-center">Order status auto-updates to &quot;shipped&quot;.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom Order Detail Dialog */}
      <Dialog open={!!coDetail} onOpenChange={(v) => !v && setCoDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-rani" /> Custom Order
            </DialogTitle>
          </DialogHeader>
          {coDetail && (
            <div className="space-y-3 text-sm">
              <div className="bg-marigold/10 rounded-xl p-3">
                <p className="font-bold text-deepbrown">{coDetail.name}</p>
                <p className="text-deepbrown/70 flex items-center gap-1"><Mail className="h-3 w-3" /> <a className="hover:text-rani" href={`mailto:${coDetail.email}`}>{coDetail.email}</a></p>
                {coDetail.phone && <p className="text-deepbrown/70 flex items-center gap-1"><Phone className="h-3 w-3" /> <a className="hover:text-rani" href={`tel:${coDetail.phone}`}>{coDetail.phone}</a></p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {coDetail.occasion && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-deepbrown/50">Occasion</p>
                    <p className="text-deepbrown">{coDetail.occasion}</p>
                  </div>
                )}
                {coDetail.budget && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-deepbrown/50">Budget</p>
                    <p className="text-deepbrown">{coDetail.budget}</p>
                  </div>
                )}
              </div>
              {coDetail.color_preferences && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-deepbrown/50">Colour / Style</p>
                  <p className="text-deepbrown">{coDetail.color_preferences}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-deepbrown/50">Description</p>
                <p className="text-deepbrown whitespace-pre-wrap mt-1 bg-ivory rounded-lg p-3">{coDetail.description}</p>
              </div>
              {coDetail.reference_image && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-deepbrown/50">Reference</p>
                  <a href={coDetail.reference_image} target="_blank" rel="noreferrer" className="text-rani break-all hover:underline">{coDetail.reference_image}</a>
                </div>
              )}
              <div className="pt-3 border-t border-deepbrown/10 flex flex-wrap gap-2">
                <a href={`mailto:${coDetail.email}?subject=Re: Your custom order request to Sutrakosh`} className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-rani text-white text-sm font-bold hover:scale-[1.02] transition-transform">
                  <Mail className="h-4 w-4" /> Reply by Email
                </a>
                {coDetail.phone && (
                  <a href={`https://wa.me/91${coDetail.phone}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald text-white text-sm font-bold hover:scale-[1.02] transition-transform">
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Cancel Dialog */}
      <Dialog open={!!cancelOrder} onOpenChange={(v) => !v && setCancelOrder(null)}>
        <DialogContent className="max-w-md" data-testid="admin-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rani" /> Cancel order
            </DialogTitle>
          </DialogHeader>
          {cancelOrder && (
            <div className="space-y-3">
              <div className="bg-marigold/10 rounded-xl p-3 text-sm">
                <p><b>{cancelOrder.order_number}</b> · {cancelOrder.user_name}</p>
                <p className="text-deepbrown/70">{cancelOrder.user_email}</p>
                <p className="text-deepbrown/70">Total: <b>₹{cancelOrder.total.toLocaleString("en-IN")}</b> · Payment: {cancelOrder.payment_method} ({cancelOrder.payment_status})</p>
              </div>
              <div>
                <Label>Reason</Label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger data-testid="admin-cancel-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                  <SelectContent>
                    {ADMIN_CANCEL_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (internal + included in customer email)</Label>
                <Textarea rows={3} value={cancelNotes} onChange={(e) => setCancelNotes(e.target.value)} placeholder="Add any context — apologies, refund timeline, link to alternative product…" data-testid="admin-cancel-notes" />
              </div>
              {cancelOrder.payment_status === "paid" && (
                <p className="text-xs bg-emerald/10 text-emerald p-2 rounded-lg">
                  Customer paid via Razorpay — please initiate refund of ₹{cancelOrder.total.toLocaleString("en-IN")} from the Razorpay dashboard. The email will mention &quot;5–7 business days&quot;.
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={() => setCancelOrder(null)} className="flex-1 py-2.5 rounded-full bg-white border-2 border-deepbrown/20 text-deepbrown text-sm font-bold">
                  Keep order
                </button>
                <button onClick={submitAdminCancel} className="flex-1 py-2.5 rounded-full bg-rani text-white text-sm font-bold" data-testid="admin-cancel-confirm">
                  Cancel & notify customer
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={catOpen} onOpenChange={(v) => { setCatOpen(v); if (!v) setEditingCat(null); }}>
        <DialogContent className="max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingCat?.id && categories.find((c) => c.id === editingCat.id) ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>
          {editingCat && (
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={editingCat.name}
                  onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })}
                  placeholder="e.g. Wedding Bouquets"
                  data-testid="cat-name"
                />
                {editingCat.id && categories.find((c) => c.id === editingCat.id) && (
                  <p className="text-[10px] text-deepbrown/50 mt-1">ID stays as <span className="font-mono">{editingCat.id}</span> — renaming only updates display name.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Icon (lucide-react)</Label>
                  <Input
                    value={editingCat.icon || "Tag"}
                    onChange={(e) => setEditingCat({ ...editingCat, icon: e.target.value })}
                    placeholder="Home, Gift, Tag…"
                    data-testid="cat-icon"
                  />
                </div>
                <div>
                  <Label>Sort order</Label>
                  <Input
                    type="number"
                    value={editingCat.sort_order ?? 99}
                    onChange={(e) => setEditingCat({ ...editingCat, sort_order: e.target.value })}
                    data-testid="cat-sort"
                  />
                </div>
              </div>
              <button onClick={saveCategory} className="w-full py-3 rounded-full bg-rani text-white font-bold" data-testid="cat-save">
                Save Category
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-deepbrown/10">
      <div className={`h-10 w-10 rounded-full ${color} text-white grid place-items-center`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-xs uppercase tracking-widest font-bold text-deepbrown/60 mt-3">{label}</p>
      <p className="font-display text-3xl text-deepbrown font-bold">{value}</p>
    </div>
  );
}
